import React, { useRef, useEffect, useState, useCallback } from 'react';
import PenWidthButton from './PenWidthButton';
import PenColorButton from './PenColorButton';
import EraserWidthButton from './EraserWidthButton';
import ShapeToolButton, { SHAPE_KEYS } from './ShapeToolButton';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faEraser,
  faPen,
  faUndo,
  faRedo,
  faTrash,
  faDownload,
} from '@fortawesome/free-solid-svg-icons';
import { IconButton } from '@material-ui/core';

import './SketchBoard.css';

const MAX_HISTORY = 30;
const MIN_HEIGHT = 480;
const HEIGHT_VH_RATIO = 0.7;

const SHAPE_LABELS = {
  line: 'Line',
  arrow: 'Arrow',
  rectangle: 'Rectangle',
  square: 'Square',
  circle: 'Circle',
  triangle: 'Triangle',
};

const isShapeTool = (tool) => SHAPE_KEYS.includes(tool);

const SketchBoard = () => {
  const canvasRef = useRef(null);
  const containerRef = useRef(null);
  const cursorRef = useRef(null);
  const isDrawingRef = useRef(false);
  const lastPointRef = useRef(null);
  const shapeStartRef = useRef(null);
  const shapeSnapshotRef = useRef(null);

  const [tool, setTool] = useState('pen');
  const [penWidth, setPenWidth] = useState(3);
  const [penColor, setPenColor] = useState('#000000');
  const [eraserWidth, setEraserWidth] = useState(20);
  const [undoStack, setUndoStack] = useState([]);
  const [redoStack, setRedoStack] = useState([]);

  const setupCanvas = useCallback(() => {
    const canvas = canvasRef.current;
    const container = containerRef.current;
    if (!canvas || !container) return;

    const dpr = window.devicePixelRatio || 1;
    const wrap = canvas.parentElement;
    const wrapRect = wrap.getBoundingClientRect();
    const cssWidth = wrapRect.width;
    const cssHeight = Math.max(MIN_HEIGHT, Math.round(window.innerHeight * HEIGHT_VH_RATIO));

    canvas.width = Math.round(cssWidth * dpr);
    canvas.height = Math.round(cssHeight * dpr);
    canvas.style.width = cssWidth + 'px';
    canvas.style.height = cssHeight + 'px';

    const ctx = canvas.getContext('2d');
    ctx.setTransform(1, 0, 0, 1, 0, 0);
    ctx.scale(dpr, dpr);
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';
  }, []);

  useEffect(() => {
    setupCanvas();
    const onResize = () => setupCanvas();
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, [setupCanvas]);

  const pushSnapshotToUndo = (snapshot) => {
    setUndoStack((stack) => {
      const next = [...stack, snapshot];
      if (next.length > MAX_HISTORY) next.shift();
      return next;
    });
    setRedoStack([]);
  };

  const captureSnapshot = () => {
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    return ctx.getImageData(0, 0, canvas.width, canvas.height);
  };

  const pushUndoSnapshot = () => {
    pushSnapshotToUndo(captureSnapshot());
  };

  const restoreSnapshot = (snapshot) => {
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    ctx.save();
    ctx.setTransform(1, 0, 0, 1, 0, 0);
    if (snapshot && snapshot.width === canvas.width && snapshot.height === canvas.height) {
      ctx.putImageData(snapshot, 0, 0);
    } else {
      ctx.clearRect(0, 0, canvas.width, canvas.height);
    }
    ctx.restore();
  };

  const undo = useCallback(() => {
    setUndoStack((undoS) => {
      if (undoS.length === 0) return undoS;
      const current = captureSnapshot();
      const prev = undoS[undoS.length - 1];
      setRedoStack((redoS) => {
        const next = [...redoS, current];
        if (next.length > MAX_HISTORY) next.shift();
        return next;
      });
      restoreSnapshot(prev);
      return undoS.slice(0, -1);
    });
  }, []);

  const redo = useCallback(() => {
    setRedoStack((redoS) => {
      if (redoS.length === 0) return redoS;
      const current = captureSnapshot();
      const nextSnap = redoS[redoS.length - 1];
      setUndoStack((undoS) => {
        const next = [...undoS, current];
        if (next.length > MAX_HISTORY) next.shift();
        return next;
      });
      restoreSnapshot(nextSnap);
      return redoS.slice(0, -1);
    });
  }, []);

  const cleanAll = () => {
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    pushUndoSnapshot();
    ctx.save();
    ctx.setTransform(1, 0, 0, 1, 0, 0);
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    ctx.restore();
  };

  const downloadImage = () => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const exportCanvas = document.createElement('canvas');
    exportCanvas.width = canvas.width;
    exportCanvas.height = canvas.height;
    const exportCtx = exportCanvas.getContext('2d');
    exportCtx.fillStyle = '#ffffff';
    exportCtx.fillRect(0, 0, exportCanvas.width, exportCanvas.height);
    exportCtx.drawImage(canvas, 0, 0);

    const stamp = new Date()
      .toISOString()
      .replace(/[:.]/g, '-')
      .slice(0, 19);
    const link = document.createElement('a');
    link.download = `sketch-${stamp}.png`;
    link.href = exportCanvas.toDataURL('image/png');
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const getPos = (event) => {
    const rect = canvasRef.current.getBoundingClientRect();
    return {
      x: event.clientX - rect.left,
      y: event.clientY - rect.top,
    };
  };

  const drawSegment = (from, to) => {
    const ctx = canvasRef.current.getContext('2d');
    ctx.save();
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';
    if (tool === 'eraser') {
      ctx.globalCompositeOperation = 'destination-out';
      ctx.lineWidth = eraserWidth;
      ctx.strokeStyle = 'rgba(0,0,0,1)';
    } else {
      ctx.lineWidth = penWidth;
      ctx.strokeStyle = penColor;
    }
    ctx.beginPath();
    ctx.moveTo(from.x, from.y);
    ctx.lineTo(to.x, to.y);
    ctx.stroke();
    ctx.restore();
  };

  const drawDot = (pos) => {
    const ctx = canvasRef.current.getContext('2d');
    ctx.save();
    if (tool === 'eraser') {
      ctx.globalCompositeOperation = 'destination-out';
      ctx.fillStyle = 'rgba(0,0,0,1)';
      ctx.beginPath();
      ctx.arc(pos.x, pos.y, eraserWidth / 2, 0, Math.PI * 2);
      ctx.fill();
    } else {
      ctx.fillStyle = penColor;
      ctx.beginPath();
      ctx.arc(pos.x, pos.y, penWidth / 2, 0, Math.PI * 2);
      ctx.fill();
    }
    ctx.restore();
  };

  const drawShape = (shape, from, to) => {
    const ctx = canvasRef.current.getContext('2d');
    ctx.save();
    ctx.strokeStyle = penColor;
    ctx.fillStyle = penColor;
    ctx.lineWidth = penWidth;
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';

    switch (shape) {
      case 'line': {
        ctx.beginPath();
        ctx.moveTo(from.x, from.y);
        ctx.lineTo(to.x, to.y);
        ctx.stroke();
        break;
      }
      case 'arrow': {
        const headLen = Math.max(10, penWidth * 4);
        const angle = Math.atan2(to.y - from.y, to.x - from.x);
        const distance = Math.hypot(to.x - from.x, to.y - from.y);
        const shaftEndX = to.x - Math.cos(angle) * Math.min(headLen * 0.6, distance * 0.4);
        const shaftEndY = to.y - Math.sin(angle) * Math.min(headLen * 0.6, distance * 0.4);
        ctx.beginPath();
        ctx.moveTo(from.x, from.y);
        ctx.lineTo(shaftEndX, shaftEndY);
        ctx.stroke();
        ctx.beginPath();
        ctx.moveTo(to.x, to.y);
        ctx.lineTo(
          to.x - headLen * Math.cos(angle - Math.PI / 7),
          to.y - headLen * Math.sin(angle - Math.PI / 7)
        );
        ctx.lineTo(
          to.x - headLen * Math.cos(angle + Math.PI / 7),
          to.y - headLen * Math.sin(angle + Math.PI / 7)
        );
        ctx.closePath();
        ctx.fill();
        break;
      }
      case 'rectangle': {
        const x = Math.min(from.x, to.x);
        const y = Math.min(from.y, to.y);
        const w = Math.abs(to.x - from.x);
        const h = Math.abs(to.y - from.y);
        ctx.strokeRect(x, y, w, h);
        break;
      }
      case 'square': {
        const dx = to.x - from.x;
        const dy = to.y - from.y;
        const size = Math.max(Math.abs(dx), Math.abs(dy));
        const x = dx < 0 ? from.x - size : from.x;
        const y = dy < 0 ? from.y - size : from.y;
        ctx.strokeRect(x, y, size, size);
        break;
      }
      case 'circle': {
        const cx = (from.x + to.x) / 2;
        const cy = (from.y + to.y) / 2;
        const r = Math.hypot(to.x - from.x, to.y - from.y) / 2;
        ctx.beginPath();
        ctx.arc(cx, cy, r, 0, Math.PI * 2);
        ctx.stroke();
        break;
      }
      case 'triangle': {
        const minX = Math.min(from.x, to.x);
        const maxX = Math.max(from.x, to.x);
        const minY = Math.min(from.y, to.y);
        const maxY = Math.max(from.y, to.y);
        const apexX = (minX + maxX) / 2;
        ctx.beginPath();
        ctx.moveTo(apexX, minY);
        ctx.lineTo(minX, maxY);
        ctx.lineTo(maxX, maxY);
        ctx.closePath();
        ctx.stroke();
        break;
      }
      default:
        break;
    }
    ctx.restore();
  };

  const handlePointerDown = (event) => {
    if (event.button !== undefined && event.button !== 0) return;
    event.preventDefault();
    canvasRef.current.setPointerCapture(event.pointerId);
    const pos = getPos(event);

    if (isShapeTool(tool)) {
      shapeSnapshotRef.current = captureSnapshot();
      shapeStartRef.current = pos;
      isDrawingRef.current = true;
    } else {
      pushUndoSnapshot();
      lastPointRef.current = pos;
      isDrawingRef.current = true;
      drawDot(pos);
    }
  };

  const handlePointerMove = (event) => {
    updateCursor(event);
    if (!isDrawingRef.current) return;
    event.preventDefault();
    const pos = getPos(event);

    if (isShapeTool(tool)) {
      if (shapeSnapshotRef.current) {
        restoreSnapshot(shapeSnapshotRef.current);
        drawShape(tool, shapeStartRef.current, pos);
      }
    } else {
      const last = lastPointRef.current;
      if (last) drawSegment(last, pos);
      lastPointRef.current = pos;
    }
  };

  const handlePointerUp = (event) => {
    if (!isDrawingRef.current) return;
    isDrawingRef.current = false;

    if (isShapeTool(tool) && shapeSnapshotRef.current) {
      if (event) {
        const pos = getPos(event);
        restoreSnapshot(shapeSnapshotRef.current);
        drawShape(tool, shapeStartRef.current, pos);
      }
      pushSnapshotToUndo(shapeSnapshotRef.current);
      shapeSnapshotRef.current = null;
      shapeStartRef.current = null;
    }
    lastPointRef.current = null;
  };

  const updateCursor = (event) => {
    const cursor = cursorRef.current;
    const canvas = canvasRef.current;
    if (!cursor || !canvas) return;
    if (isShapeTool(tool)) {
      cursor.style.opacity = '0';
      return;
    }
    const rect = canvas.getBoundingClientRect();
    const x = event.clientX - rect.left;
    const y = event.clientY - rect.top;
    const size = tool === 'eraser' ? eraserWidth : Math.max(penWidth, 6);
    cursor.style.width = size + 'px';
    cursor.style.height = size + 'px';
    cursor.style.transform = `translate(${x - size / 2}px, ${y - size / 2}px)`;
    cursor.style.opacity = '1';
  };

  const hideCursor = () => {
    const cursor = cursorRef.current;
    if (cursor) cursor.style.opacity = '0';
  };

  useEffect(() => {
    const onKey = (event) => {
      const target = event.target;
      const tag = target && target.tagName;
      if (tag === 'INPUT' || tag === 'TEXTAREA' || (target && target.isContentEditable)) return;

      const mod = event.ctrlKey || event.metaKey;
      if (mod && event.key.toLowerCase() === 'z' && !event.shiftKey) {
        event.preventDefault();
        undo();
      } else if (
        mod &&
        (event.key.toLowerCase() === 'y' || (event.shiftKey && event.key.toLowerCase() === 'z'))
      ) {
        event.preventDefault();
        redo();
      } else if (mod && event.key.toLowerCase() === 's') {
        event.preventDefault();
        downloadImage();
      } else if (event.key.toLowerCase() === 'p' && !mod) {
        setTool('pen');
      } else if (event.key.toLowerCase() === 'e' && !mod) {
        setTool('eraser');
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [undo, redo]);

  const isPen = tool === 'pen';
  const isEraser = tool === 'eraser';
  const shapeActive = isShapeTool(tool);

  const canvasModifier = isEraser
    ? ' sb-canvas--eraser'
    : shapeActive
    ? ' sb-canvas--shape'
    : ' sb-canvas--pen';

  return (
    <div className="sketch-board" ref={containerRef}>
      <div className="sketch-board-toolbar">
        <div className="toolbar-group" role="group" aria-label="Drawing tool">
          <IconButton
            onClick={() => setTool('pen')}
            className={'sb-icon-btn' + (isPen ? ' sb-icon-btn--active' : '')}
            title="Pen (P)"
            aria-pressed={isPen}
          >
            <FontAwesomeIcon icon={faPen} />
          </IconButton>
          <IconButton
            onClick={() => setTool('eraser')}
            className={'sb-icon-btn' + (isEraser ? ' sb-icon-btn--active' : '')}
            title="Eraser (E)"
            aria-pressed={isEraser}
          >
            <FontAwesomeIcon icon={faEraser} />
          </IconButton>
          <ShapeToolButton tool={tool} onSelectShape={setTool} />
        </div>

        <span className="toolbar-divider" aria-hidden="true" />

        <div className="toolbar-group" role="group" aria-label="Tool options">
          {isEraser ? (
            <EraserWidthButton onSelectWidth={setEraserWidth} />
          ) : (
            <>
              <PenWidthButton onSelectWidth={setPenWidth} />
              <PenColorButton onSelectColor={setPenColor} />
            </>
          )}
        </div>

        <span className="toolbar-divider" aria-hidden="true" />

        <div className="toolbar-group" role="group" aria-label="History">
          <IconButton
            onClick={undo}
            disabled={undoStack.length === 0}
            className="sb-icon-btn"
            title="Undo (Ctrl+Z)"
          >
            <FontAwesomeIcon icon={faUndo} />
          </IconButton>
          <IconButton
            onClick={redo}
            disabled={redoStack.length === 0}
            className="sb-icon-btn"
            title="Redo (Ctrl+Y)"
          >
            <FontAwesomeIcon icon={faRedo} />
          </IconButton>
        </div>

        <span className="toolbar-divider" aria-hidden="true" />

        <div className="toolbar-group">
          <IconButton
            onClick={downloadImage}
            className="sb-icon-btn"
            title="Download as PNG (Ctrl+S)"
          >
            <FontAwesomeIcon icon={faDownload} />
          </IconButton>
          <IconButton
            onClick={cleanAll}
            className="sb-icon-btn sb-icon-btn--danger"
            title="Clear all"
          >
            <FontAwesomeIcon icon={faTrash} />
          </IconButton>
        </div>

        <div className="toolbar-spacer" />

        <div className="toolbar-status" aria-live="polite">
          {isEraser ? (
            <span className="status-text">Eraser · {eraserWidth}px</span>
          ) : (
            <>
              <span
                className="status-swatch"
                style={{ backgroundColor: penColor }}
                aria-label={'Current color ' + penColor}
              />
              <span className="status-text">
                {shapeActive ? SHAPE_LABELS[tool] : 'Pen'} · {penWidth}px
              </span>
            </>
          )}
        </div>
      </div>

      <div className="sketch-canvas-wrap">
        <canvas
          id="canvas"
          ref={canvasRef}
          className={'sketch-canvas' + canvasModifier}
          onPointerDown={handlePointerDown}
          onPointerMove={handlePointerMove}
          onPointerUp={handlePointerUp}
          onPointerCancel={handlePointerUp}
          onPointerLeave={hideCursor}
          onPointerEnter={updateCursor}
        />
        <div ref={cursorRef} className="sb-cursor-preview" aria-hidden="true" />
      </div>
    </div>
  );
};

export default SketchBoard;
